import { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  CircularProgress,
  Paper,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tooltip,
  Stack,
  Tabs,
  Tab,
  Grid,
  Divider,
} from '@mui/material';
import {
  Visibility,
  CheckCircle,
  Cancel,
  Business,
  Person,
  Email,
  Phone,
  Language,
  LocationOn,
} from '@mui/icons-material';
import { formatDateOnly } from '@/utils/formatDate';
import { getToken } from '@/utils/storage';

const API_BASE = import.meta.env.VITE_API_URL || 'https://ninshousystem-api-361153976392.asia-northeast1.run.app';

interface PartnerApplication {
  id: number;
  company_name: string;
  company_url: string;
  company_address: string;
  company_phone: string | null;
  contact_name: string;
  contact_email: string;
  contact_phone: string | null;
  plan: string;
  status: 'pending' | 'reviewing' | 'approved' | 'rejected';
  review_notes: string | null;
  rejection_reason: string | null;
  tenant_id: number | null;
  created_at: string;
  reviewed_at: string | null;
}

interface ApplicationListResponse {
  total: number;
  pending: number;
  reviewing: number;
  approved: number;
  rejected: number;
  items: PartnerApplication[];
}

export const PartnerApplicationsPage = () => {
  const [applications, setApplications] = useState<PartnerApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [counts, setCounts] = useState({ total: 0, pending: 0, reviewing: 0, approved: 0, rejected: 0 });

  // タブ状態
  const [activeTab, setActiveTab] = useState(0);

  // ダイアログ状態
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState<PartnerApplication | null>(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');

  // 承認結果
  const [approvalResult, setApprovalResult] = useState<{
    tenant_id: number;
    tenant_code: string;
    admin_email: string;
    temp_password: string;
  } | null>(null);

  const statusFilters = ['', 'pending', 'reviewing', 'approved', 'rejected'];

  const fetchApplications = async () => {
    try {
      setLoading(true);
      const statusFilter = statusFilters[activeTab];
      const queryString = statusFilter ? `?status=${statusFilter}` : '';
      const res = await fetch(`${API_BASE}/admin/partner-applications${queryString}`, {
        headers: {
          'Authorization': `Bearer ${getToken()}`,
        },
      });
      if (!res.ok) throw new Error('Failed to fetch applications');
      const data: ApplicationListResponse = await res.json();
      setApplications(data.items);
      setCounts({
        total: data.total,
        pending: data.pending,
        reviewing: data.reviewing,
        approved: data.approved,
        rejected: data.rejected,
      });
    } catch (err) {
      setError('申請一覧の取得に失敗しました');
      console.error('Fetch applications error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchApplications();
  }, [activeTab]);

  const handleStartReview = async (app: PartnerApplication) => {
    try {
      const res = await fetch(`${API_BASE}/admin/partner-applications/${app.id}/review`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
        },
      });
      if (!res.ok) throw new Error('Failed to start review');
      setSuccess('審査を開始しました');
      fetchApplications();
    } catch (err) {
      setError('審査開始に失敗しました');
    }
  };

  const handleApprove = async () => {
    if (!selectedApplication) return;
    try {
      const res = await fetch(`${API_BASE}/admin/partner-applications/${selectedApplication.id}/approve`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          review_notes: reviewNotes || null,
        }),
      });
      if (!res.ok) throw new Error('Failed to approve');
      const data = await res.json();
      setApprovalResult(data);
      setSuccess(`${selectedApplication.company_name} を承認しました`);
      setApproveDialogOpen(false);
      setReviewNotes('');
      fetchApplications();
    } catch (err) {
      setError('承認に失敗しました');
    }
  };

  const handleReject = async () => {
    if (!selectedApplication) return;
    try {
      const res = await fetch(`${API_BASE}/admin/partner-applications/${selectedApplication.id}/reject`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          rejection_reason: rejectionReason,
        }),
      });
      if (!res.ok) throw new Error('Failed to reject');
      setSuccess(`${selectedApplication.company_name} を却下しました`);
      setRejectDialogOpen(false);
      setRejectionReason('');
      fetchApplications();
    } catch (err) {
      setError('却下に失敗しました');
    }
  };

  const getStatusChip = (status: string) => {
    const config: Record<string, { label: string; color: 'default' | 'warning' | 'info' | 'success' | 'error' }> = {
      pending: { label: '審査待ち', color: 'warning' },
      reviewing: { label: '審査中', color: 'info' },
      approved: { label: '承認済み', color: 'success' },
      rejected: { label: '却下', color: 'error' },
    };
    const { label, color } = config[status] || { label: status, color: 'default' };
    return <Chip label={label} color={color} size="small" />;
  };

  const getPlanChip = (plan: string) => {
    const config: Record<string, { label: string; color: 'primary' | 'secondary' }> = {
      standard: { label: 'Standard', color: 'primary' },
      unlimited: { label: 'Unlimited', color: 'secondary' },
    };
    const { label, color } = config[plan] || { label: plan, color: 'primary' };
    return <Chip label={label} color={color} size="small" variant="outlined" />;
  };

  if (loading && applications.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          パートナー申請管理
        </Typography>
        <Typography variant="body1" color="text.secondary">
          OEMパートナー申請の審査・承認
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* 統計カード */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h4" color="warning.main">{counts.pending}</Typography>
              <Typography variant="body2" color="text.secondary">審査待ち</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h4" color="info.main">{counts.reviewing}</Typography>
              <Typography variant="body2" color="text.secondary">審査中</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h4" color="success.main">{counts.approved}</Typography>
              <Typography variant="body2" color="text.secondary">承認済み</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h4" color="error.main">{counts.rejected}</Typography>
              <Typography variant="body2" color="text.secondary">却下</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* タブフィルター */}
      <Card>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label={`すべて (${counts.total})`} />
          <Tab label={`審査待ち (${counts.pending})`} />
          <Tab label={`審査中 (${counts.reviewing})`} />
          <Tab label={`承認済み (${counts.approved})`} />
          <Tab label={`却下 (${counts.rejected})`} />
        </Tabs>
        <CardContent>
          <TableContainer component={Paper} elevation={0}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>会社名</TableCell>
                  <TableCell>担当者</TableCell>
                  <TableCell>プラン</TableCell>
                  <TableCell>ステータス</TableCell>
                  <TableCell>申請日</TableCell>
                  <TableCell align="right">操作</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {applications.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">申請がありません</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  applications.map((app) => (
                    <TableRow key={app.id}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Business color="action" />
                          <Box>
                            <Typography fontWeight="medium">{app.company_name}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {app.company_url}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography>{app.contact_name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {app.contact_email}
                        </Typography>
                      </TableCell>
                      <TableCell>{getPlanChip(app.plan)}</TableCell>
                      <TableCell>{getStatusChip(app.status)}</TableCell>
                      <TableCell>{formatDateOnly(app.created_at)}</TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                          <Tooltip title="詳細">
                            <IconButton
                              size="small"
                              onClick={() => {
                                setSelectedApplication(app);
                                setDetailDialogOpen(true);
                              }}
                            >
                              <Visibility />
                            </IconButton>
                          </Tooltip>
                          {app.status === 'pending' && (
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => handleStartReview(app)}
                            >
                              審査開始
                            </Button>
                          )}
                          {(app.status === 'pending' || app.status === 'reviewing') && (
                            <>
                              <Tooltip title="承認">
                                <IconButton
                                  size="small"
                                  color="success"
                                  onClick={() => {
                                    setSelectedApplication(app);
                                    setApproveDialogOpen(true);
                                  }}
                                >
                                  <CheckCircle />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="却下">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => {
                                    setSelectedApplication(app);
                                    setRejectDialogOpen(true);
                                  }}
                                >
                                  <Cancel />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* 詳細ダイアログ */}
      <Dialog open={detailDialogOpen} onClose={() => setDetailDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>申請詳細</DialogTitle>
        <DialogContent>
          {selectedApplication && (
            <Stack spacing={3} sx={{ mt: 1 }}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  会社情報
                </Typography>
                <Stack spacing={1}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Business fontSize="small" color="action" />
                    <Typography>{selectedApplication.company_name}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Language fontSize="small" color="action" />
                    <Typography>
                      <a href={selectedApplication.company_url} target="_blank" rel="noopener noreferrer">
                        {selectedApplication.company_url}
                      </a>
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LocationOn fontSize="small" color="action" />
                    <Typography>{selectedApplication.company_address}</Typography>
                  </Box>
                  {selectedApplication.company_phone && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Phone fontSize="small" color="action" />
                      <Typography>{selectedApplication.company_phone}</Typography>
                    </Box>
                  )}
                </Stack>
              </Box>

              <Divider />

              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  担当者情報
                </Typography>
                <Stack spacing={1}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Person fontSize="small" color="action" />
                    <Typography>{selectedApplication.contact_name}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Email fontSize="small" color="action" />
                    <Typography>{selectedApplication.contact_email}</Typography>
                  </Box>
                  {selectedApplication.contact_phone && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Phone fontSize="small" color="action" />
                      <Typography>{selectedApplication.contact_phone}</Typography>
                    </Box>
                  )}
                </Stack>
              </Box>

              <Divider />

              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  プラン・ステータス
                </Typography>
                <Stack direction="row" spacing={2}>
                  {getPlanChip(selectedApplication.plan)}
                  {getStatusChip(selectedApplication.status)}
                </Stack>
              </Box>

              {selectedApplication.rejection_reason && (
                <>
                  <Divider />
                  <Box>
                    <Typography variant="subtitle2" color="error" gutterBottom>
                      却下理由
                    </Typography>
                    <Typography>{selectedApplication.rejection_reason}</Typography>
                  </Box>
                </>
              )}

              {selectedApplication.review_notes && (
                <>
                  <Divider />
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      審査ノート
                    </Typography>
                    <Typography>{selectedApplication.review_notes}</Typography>
                  </Box>
                </>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialogOpen(false)}>閉じる</Button>
        </DialogActions>
      </Dialog>

      {/* 承認ダイアログ */}
      <Dialog open={approveDialogOpen} onClose={() => setApproveDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>パートナー申請を承認</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            承認すると、テナントと管理者アカウントが自動作成されます。
          </Alert>
          <Typography sx={{ mb: 2 }}>
            <strong>{selectedApplication?.company_name}</strong> を承認しますか？
          </Typography>
          <TextField
            label="審査ノート（任意）"
            value={reviewNotes}
            onChange={(e) => setReviewNotes(e.target.value)}
            multiline
            rows={3}
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveDialogOpen(false)}>キャンセル</Button>
          <Button variant="contained" color="success" onClick={handleApprove}>
            承認
          </Button>
        </DialogActions>
      </Dialog>

      {/* 承認結果ダイアログ */}
      <Dialog open={!!approvalResult} onClose={() => setApprovalResult(null)} maxWidth="sm" fullWidth>
        <DialogTitle>承認完了</DialogTitle>
        <DialogContent>
          <Alert severity="success" sx={{ mb: 2 }}>
            テナントと管理者アカウントを作成しました。
          </Alert>
          <Stack spacing={2}>
            <Box>
              <Typography variant="subtitle2" gutterBottom>テナントコード</Typography>
              <Typography fontFamily="monospace">{approvalResult?.tenant_code}</Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" gutterBottom>管理者メール</Typography>
              <Typography>{approvalResult?.admin_email}</Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" gutterBottom>仮パスワード</Typography>
              <Alert severity="warning" sx={{ mt: 1 }}>
                このパスワードは一度しか表示されません。パートナーに安全に伝えてください。
              </Alert>
              <TextField
                value={approvalResult?.temp_password || ''}
                fullWidth
                InputProps={{ readOnly: true }}
                sx={{ mt: 1, fontFamily: 'monospace' }}
              />
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApprovalResult(null)} variant="contained">
            閉じる
          </Button>
        </DialogActions>
      </Dialog>

      {/* 却下ダイアログ */}
      <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>パートナー申請を却下</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            <strong>{selectedApplication?.company_name}</strong> を却下しますか？
          </Typography>
          <TextField
            label="却下理由"
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            multiline
            rows={3}
            fullWidth
            required
            error={!rejectionReason}
            helperText={!rejectionReason ? '却下理由を入力してください' : ''}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialogOpen(false)}>キャンセル</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleReject}
            disabled={!rejectionReason}
          >
            却下
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PartnerApplicationsPage;
